import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

/**
 * How many programs something covers, and which ones behind a click.
 *
 * The keys used to be printed inline — `TH-33628 TH-34681 TH-44491` — which is what an access check
 * reads, not what anyone recognises. Names are not in the listing payloads and resolving them costs
 * a query, so they are fetched the first time someone asks and kept until the set itself changes.
 */
const ProgramsSummary = ({ projectkeys = [], hiddenCount = 0, request, title }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [programs, setPrograms] = useState(null);
  const [error, setError] = useState(null);

  const keys = projectkeys.join(',');

  useEffect(() => {
    setPrograms(null);
    setError(null);
  }, [keys]);

  useEffect(() => {
    if (!open || programs || error || !keys) return;
    request(`/admin/programs?projectkeys=${encodeURIComponent(keys)}`)
      .then(setPrograms)
      .catch(caught => setError(caught.message));
  }, [open, programs, error, keys, request]);

  if (projectkeys.length === 0 && hiddenCount === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('None yet.')}</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {projectkeys.length > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-800 dark:hover:bg-blue-950/50 dark:hover:text-blue-200"
        >
          {t('programCount', { count: projectkeys.length })}
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-3 w-3 opacity-60" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      {hiddenCount > 0 && (
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {t('+{{count}} not visible to you', { count: hiddenCount })}
        </span>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={title || t('Programs')} width="max-w-xl">
        {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{error}</p>}
        {!error && !programs && <p className="text-sm text-gray-500 dark:text-gray-400">{t('Loading…')}</p>}
        {programs && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {programs.map((program) => {
              // An unnamed program is already titled by its key: repeating it underneath says nothing.
              const meta = [program.client, program.projectname ? program.projectkey : null].filter(Boolean);
              return (
                <li key={program.projectkey} className="py-2.5">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{program.projectname || program.projectkey}</p>
                  {meta.length > 0 && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{meta.join(' · ')}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </Modal>
    </div>
  );
};

export default ProgramsSummary;
